import axios, { AxiosError } from 'axios';
import LocalStorageService from 'services/LocalStorageService';
import {
  LOCAL_STORAGE_ACCESS_TOKEN,
  LOCAL_STORAGE_REFRESH_TOKEN,
} from './constants';
import { IRootReducerState, store } from '../../store/store';
import { config } from 'config';
import { LOGOUT } from 'store/constants';
import i18n, { FALLBACK_LOCALE } from 'providers/i18n/i18n';
import { queryClient } from 'index';
import { ICompany } from 'types/Company.types';
import { decodeTokenAndSetUserData } from 'pages/Auth/LoginPage/helpers';
import * as Sentry from '@sentry/react';
import { validationCaptureContext } from './validationCapture';
import { newRequestId, REQUEST_ID_HEADER } from './requestId';

const API_URL = config.api.baseUrl;
const AUTH_BASE_URL = API_URL;

const apiClient = axios.create({
  baseURL: API_URL,
});

const apiAuthClient = axios.create({
  baseURL: AUTH_BASE_URL,
});

apiAuthClient.interceptors.request.use(
  async (config) => {
    // Include locale header, and the request's own Authorization: a company
    // activation link's activation token (CompanyService). And a fresh request
    // id, which Logyx core logs and echoes (requestId.ts).
    config.headers = {
      ...(config.headers?.Authorization && {
        Authorization: config.headers.Authorization,
      }),
      'X-Locale': i18n.resolvedLanguage || FALLBACK_LOCALE,
      [REQUEST_ID_HEADER]: newRequestId(),
    };
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.request.use(
  async (config) => {
    const state: IRootReducerState = store.getState();
    const intentUUID = state.submitConfigurationInfo.intentUUID;
    const token = LocalStorageService.getItem(LOCAL_STORAGE_ACCESS_TOKEN);
    if (!token && !intentUUID) {
      store.dispatch({ type: LOGOUT });
    }
    // Include authorization and locale headers, and a fresh request id, which
    // Logyx core logs and echoes (requestId.ts)
    config.headers = {
      Authorization: intentUUID ? `Bearer ${intentUUID}` : `Bearer ${token}`,
      'X-Locale': i18n.resolvedLanguage || FALLBACK_LOCALE,
      [REQUEST_ID_HEADER]: newRequestId(),
      // 'ngrok-skip-browser-warning': '69420',
    };
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // A request that got no answer (a network error, a timeout) has no
    // `response`. Every path below rejects with an error: one that resolved
    // instead would hand its caller `undefined` as if the request had worked.
    const originalRequest = error?.config;
    const status = error?.response?.status;
    try {
      // Capture 422 responses with Sentry: which call and which fields, never
      // the request's body or the answer (validationCapture.ts).
      if (status === 422) {
        Sentry.captureException(
          error,
          validationCaptureContext(originalRequest, error.response.data)
        );
      }

      const currentRefreshToken = LocalStorageService.getItem(
        LOCAL_STORAGE_REFRESH_TOKEN
      );

      const lastRefreshRequest = parseInt(
        LocalStorageService.getItem('LAST_REFRESH_REQUEST_TIMESTAMP') || '0'
      );
      const currentTime = new Date().getTime();
      const hasMinutePassed = currentTime - lastRefreshRequest > 5000;

      if (
        currentRefreshToken &&
        status === 401 &&
        originalRequest &&
        !originalRequest._retry &&
        hasMinutePassed
      ) {
        originalRequest._retry = true;
        LocalStorageService.setItem(
          'LAST_REFRESH_REQUEST_TIMESTAMP',
          currentTime.toString()
        );
        const {
          company,
        }: {
          company: ICompany | undefined;
        } = store.getState().userInfo;
        const refreshResponse = await apiAuthClient.post('/v1/refresh', {
          refresh_token: currentRefreshToken,
          company_id: company?.id,
        });
        const { access_token, refresh_token } = refreshResponse.data.data;
        decodeTokenAndSetUserData(access_token, store.dispatch);

        await LocalStorageService.setItem(
          LOCAL_STORAGE_ACCESS_TOKEN,
          access_token
        );
        await LocalStorageService.setItem(
          LOCAL_STORAGE_REFRESH_TOKEN,
          refresh_token
        );

        // Potentially multiple requests failed at the moment of refresh token expiration so refetch everything
        queryClient.invalidateQueries();

        // The retry keeps the first request's headers, its request id too: it
        // is the same request (bare axios runs no interceptor).
        originalRequest.headers['Authorization'] = `Bearer ${access_token}`;
        return axios(originalRequest);
      }
      return Promise.reject(error);
    } catch (err) {
      if (err instanceof AxiosError) {
        if (err.response?.status === 401 || err.response?.status === 400) {
          await LocalStorageService.removeItem(LOCAL_STORAGE_ACCESS_TOKEN);
          await LocalStorageService.removeItem(LOCAL_STORAGE_REFRESH_TOKEN);
          store.dispatch({ type: LOGOUT });
        }
        return Promise.reject(err);
      }
      return Promise.reject(error);
    }
  }
);
export { apiAuthClient };
export default apiClient;
